import type { ChartRow, ChartScalar, ChartSeries, ChartSpec } from './types';

export type PriceBaseline = {
  axisId: string;
  seriesId: string;
  x: string | number | boolean | null;
  value: number;
};

function finiteNumber(value: ChartScalar | undefined): number | null {
  if (
    value === null ||
    value === undefined ||
    value === true ||
    value === false ||
    String(value).trim() === ''
  )
    return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function seriesKeys(series: ChartSeries): string[] {
  if (series.mark === 'candlestick' && series.fieldMapping) {
    return Object.values(series.fieldMapping);
  }
  return series.dataKey ? [series.dataKey] : [];
}

function primaryValue(row: ChartRow, series: ChartSeries): number | null {
  const key = series.mark === 'candlestick' ? series.fieldMapping?.close : series.dataKey;
  return key ? finiteNumber(row[key]) : null;
}

function transformValue(value: number, baseline: number, mode: 'percentage' | 'indexed'): number {
  return mode === 'indexed' ? (value / baseline) * 100 : (value / baseline - 1) * 100;
}

export type ChartDisplayModel = {
  spec: ChartSpec;
  baseline: PriceBaseline | null;
  baselines: PriceBaseline[];
};

export function buildDisplaySpec(
  spec: ChartSpec,
  mode: 'linear' | 'log' | 'percentage' | 'indexed',
  visibleRange: [number, number],
): ChartDisplayModel {
  if (mode !== 'percentage' && mode !== 'indexed') {
    return { spec, baseline: null, baselines: [] };
  }

  const [start, end] = visibleRange;
  const priceSeries = spec.series.filter((series) => series.role === 'price');
  const baselines = priceSeries.flatMap((series): PriceBaseline[] => {
    const row = spec.data
      .slice(start, end + 1)
      .find((candidate) => primaryValue(candidate, series) !== null);
    const value = row ? primaryValue(row, series) : null;
    return row && value !== null && value !== 0
      ? [{ axisId: series.axisId, seriesId: series.id, x: row[spec.xAxis.key], value }]
      : [];
  });
  const byAxis = new Map(baselines.map((baseline) => [baseline.axisId, baseline]));
  const keysByAxis = new Map<string, Set<string>>();
  spec.series.forEach((series) => {
    if (!byAxis.has(series.axisId) || series.role === 'volume') return;
    const keys = keysByAxis.get(series.axisId) || new Set<string>();
    seriesKeys(series).forEach((key) => keys.add(key));
    keysByAxis.set(series.axisId, keys);
  });

  const transformedData = spec.data.map((row) => {
    const next = { ...row };
    keysByAxis.forEach((keys, axisId) => {
      const baseline = byAxis.get(axisId)!;
      keys.forEach((key) => {
        const value = finiteNumber(row[key]);
        if (value !== null) next[key] = transformValue(value, baseline.value, mode);
      });
    });
    return next;
  });
  const transformForAxis = (axisId: string, value: number | undefined): number | undefined => {
    const baseline = byAxis.get(axisId);
    return baseline && value !== undefined ? transformValue(value, baseline.value, mode) : value;
  };
  const markerAxis = new Map(spec.series.map((series) => [series.id, series.axisId]));

  return {
    baseline: baselines[0] || null,
    baselines,
    spec: {
      ...spec,
      data: transformedData,
      axes: spec.axes.map((axis) =>
        byAxis.has(axis.id)
          ? { ...axis, format: mode === 'percentage' ? 'percent' : 'number', scale: mode }
          : axis,
      ),
      referenceLines: spec.referenceLines?.map((line) => ({
        ...line,
        value: transformForAxis(line.axisId, line.value)!,
      })),
      referenceBands: spec.referenceBands?.map((band) => ({
        ...band,
        yStart: transformForAxis(band.axisId, band.yStart)!,
        yEnd: transformForAxis(band.axisId, band.yEnd)!,
      })),
      areas: spec.areas?.map((area) => ({
        ...area,
        yStart: area.axisId ? transformForAxis(area.axisId, area.yStart) : area.yStart,
        yEnd: area.axisId ? transformForAxis(area.axisId, area.yEnd) : area.yEnd,
      })),
      markers: spec.markers?.map((marker) => {
        const axisId = markerAxis.get(marker.seriesId);
        return {
          ...marker,
          value: axisId ? transformForAxis(axisId, marker.value) : marker.value,
        };
      }),
    },
  };
}

export function positiveLogDomain(
  spec: ChartSpec,
  axisId: string,
  visibleRange: [number, number],
): [number, number] | null {
  const values: number[] = [];
  const axisSeries = spec.series.filter(
    (series) => series.axisId === axisId && series.role !== 'volume',
  );
  spec.data.slice(visibleRange[0], visibleRange[1] + 1).forEach((row) => {
    axisSeries.forEach((series) =>
      seriesKeys(series).forEach((key) => {
        const value = finiteNumber(row[key]);
        if (value !== null) values.push(value);
      }),
    );
  });
  spec.referenceLines
    ?.filter((line) => line.axisId === axisId)
    .forEach((line) => values.push(line.value));
  spec.referenceBands
    ?.filter((band) => band.axisId === axisId)
    .forEach((band) => values.push(band.yStart, band.yEnd));
  spec.areas
    ?.filter((area) => area.axisId === axisId)
    .forEach((area) => {
      if (area.yStart !== undefined) values.push(area.yStart);
      if (area.yEnd !== undefined) values.push(area.yEnd);
    });
  const seriesAxes = new Map(spec.series.map((series) => [series.id, series.axisId]));
  spec.markers
    ?.filter((marker) => seriesAxes.get(marker.seriesId) === axisId)
    .forEach((marker) => {
      if (marker.value !== undefined) values.push(marker.value);
    });
  if (values.length === 0 || values.some((value) => !Number.isFinite(value) || value <= 0))
    return null;
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  if (minimum === maximum) return [minimum / 1.03, maximum * 1.03];
  const logSpan = Math.log(maximum) - Math.log(minimum);
  const padding = Math.exp(Math.max(logSpan * 0.03, Math.log(1.01)));
  return [minimum / padding, maximum * padding];
}

export type CandlePixelGeometry = {
  wickTop: number;
  wickBottom: number;
  bodyTop: number;
  bodyBottom: number;
};

export function candlePixelGeometry(
  values: { open: number; high: number; low: number; close: number },
  scale: (value: number) => number,
): CandlePixelGeometry {
  const highY = scale(values.high);
  const lowY = scale(values.low);
  const openY = scale(values.open);
  const closeY = scale(values.close);
  return {
    wickTop: Math.min(highY, lowY),
    wickBottom: Math.max(highY, lowY),
    bodyTop: Math.min(openY, closeY),
    bodyBottom: Math.max(openY, closeY),
  };
}
